/**
 * @author David Ontiveros.
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../modules/security/service/auth.service';
import { UIDialogService } from '../../services/ui-dialog.service';
import { DataTableUtils } from '../../utils/data-table.utils';
import { SortUtils } from '../../utils/sort.utils';
import { APIOperableComponent } from '../api-operable/api-operable.component';
import { LinkedItemsListManageDialogComponent } from '../linked-items-list-manage-dialog/linked-items-list-manage-dialog.component';

export interface UpdateResultEvent {
  newItems: Array<any>;
  removedItems: Array<any>;
}

@Component({
  selector: 'linked-items-list-view',
  templateUrl:
    '/console/app-js/shared/components/linked-items-list-view/linked-items-list-view.component.html'
})
export class LinkedItemsListViewComponent extends APIOperableComponent
  implements OnInit {
  @Input('linkedItems') private linkedItems: Array<any>;
  @Input('getAllItemsOperation') private getAllItemsOperation: () => any;
  @Input('allItems') private allItems: Array<any>;
  @Input('isCreateView') isCreateView = false;
  @Input('disableManage') disableManage: boolean;
  @Input('title') title: string;
  @Output('onUpdate') onUpdate: EventEmitter<
    UpdateResultEvent
  > = new EventEmitter();
  protected originalData: any[];
  private filterValue = '';

  constructor(
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    private dialogService: UIDialogService
  ) {
    super(progressService, toastr, authService);
  }

  ngOnInit(): void {
    this.originalData = [...this.linkedItems];
  }

  /**
   * Loads All Items available to link
   */
  private loadItemsForCreateMode(): void {
    this.getAllItemsOperation().subscribe(res => {
      let results = res.map(los => {
        return {
          id: los.id,
          value: los.name
        };
      });
      this.linkedItems = results.sort((a, b) =>
        SortUtils.compareByProperty(a, b, 'value')
      );
      this.originalData = [...this.linkedItems];
    });
  }

  /**
   * Clears Filter value and updates list.
   */
  private onClearFilter(): void {
    this.filterValue = '';
    this.onFilterData();
  }

  /**
   * Filters out the table results table based on text input element.
   */
  private onFilterData(): void {
    this.linkedItems = DataTableUtils.filterDataByProperty(
      this.linkedItems,
      this.originalData,
      this.filterValue,
      'value'
    );
  }

  /**
   * Opens dialog popup to manage available items to link.
   */
  private onManage(): void {
    this.dialogService
      .open(LinkedItemsListManageDialogComponent, [
        {
          provide: Array,
          useValue: this.originalData
        },
        {
          provide: Function,
          useValue: this.getAllItemsOperation
        },
        {
          provide: 'allItems',
          useValue: this.allItems
        },
        {
          provide: 'title',
          useValue: this.title
        },
        {
          provide: 'property',
          useValue: 'number'
        },
        {
          provide: 'idField',
          useValue: 'number'
        }
      ])
      .then(results => {
        if (this.isCreateView) {
          this.doUpdateForCreate(results);
        } else {
          this.doUpdate(results);
        }
      })
      .catch(result => {
        if (result) {
          console.log(result);
        }
      });
  }

  /**
   * Update when create view is displayed, it only simulates insertions and store changes.
   * @param {Array<any>} results
   */
  private doUpdateForCreate(results: Array<any>): void {
    this.originalData = results.sort((a, b) =>
      SortUtils.compareByProperty(a, b, 'value')
    );
    this.linkedItems = [...this.originalData];
    this.onFilterData();
    this.onUpdate.emit({ newItems: this.linkedItems, removedItems: [] });
  }

  /**
   * Call the API and  Add/Delete values from the Difference with the original Data
   * @param {Array<any>} results
   */
  private doUpdate(results: Array<any>): void {
    let diffNewSelection = results.filter(
      r => !this.originalData.find(l => r.id === l.id)
    );
    let diffDeletedSelection = this.originalData.filter(
      r => !results.find(l => r.id === l.id)
    );
    this.onUpdate.emit({
      newItems: diffNewSelection,
      removedItems: diffDeletedSelection
    });
  }

  /**
   * Exposed method to reload component lists.
   * @returns {Array<any>}
   */
  public reloadLinkedItems(data: Array<any>): void {
    this.originalData = [...data];
    this.onFilterData();
  }
}
